'use client';

import React, { useState } from 'react';
import Link from 'next/link';
import { Menu, X, ChevronDown, Download } from 'lucide-react';
import { NavItem } from '@/lib/types';
import { cn } from '@/lib/utils';

interface HeaderProps {
  navigation: NavItem[];
  currentSeason: string;
}

export default function Header({ navigation, currentSeason }: HeaderProps) {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null);

  return (
    <header className="bg-navy/95 fixed top-0 right-0 left-0 z-50 py-3 shadow-lg backdrop-blur-md">
      <div className="container mx-auto flex items-center justify-between px-4">
        {/* Logo */}
        <Link href="/" className="flex flex-shrink-0 items-center">
          <picture>
            <source
              media="(max-width: 640px)"
              srcSet="/images/Logo-490x200-white-color-1.mobile.webp"
            />
            <img
              src="/images/Logo-490x200-white-color-1.webp"
              alt="Dynamic Club"
              width="612"
              height="250"
              className="h-12 w-auto object-contain md:h-16 lg:h-20"
            />
          </picture>
        </Link>

        {/* Desktop Navigation */}
        <nav className="hidden items-center lg:flex lg:gap-4 xl:gap-8">
          {navigation.map((item) => (
            <div
              key={item.label}
              className="group relative"
              onMouseEnter={() => item.children.length > 0 && setActiveDropdown(item.label)}
              onMouseLeave={() => setActiveDropdown(null)}
            >
              <Link
                href={item.url}
                onClick={(e) => {
                  if (item.url === '#' || item.children.length > 0) {
                    e.preventDefault();
                  }
                }}
                className="flex items-center gap-1 text-sm font-bold tracking-wider text-white/90 uppercase hover:text-white"
              >
                {item.label}
                {item.children.length > 0 && <ChevronDown size={14} />}
              </Link>

              {item.children.length > 0 && (
                <div
                  className={cn(
                    'absolute top-full left-0 mt-2 w-56 origin-top overflow-hidden rounded-lg bg-white shadow-xl transition-all duration-200',
                    activeDropdown === item.label
                      ? 'scale-y-100 opacity-100'
                      : 'scale-y-0 opacity-0',
                  )}
                >
                  {item.children.map((child) => (
                    <Link
                      key={child.label}
                      href={child.url}
                      className="text-navy hover:text-red block border-b border-gray-100 px-4 py-3 text-sm transition-colors last:border-0 hover:bg-gray-50"
                    >
                      {child.label}
                    </Link>
                  ))}
                </div>
              )}
            </div>
          ))}
        </nav>

        {/* Action buttons */}
        <div className="flex items-center gap-2 md:gap-4">
          <a
            href="/images/menu/BrochureDynamicClub26-27.pdf"
            target="_blank"
            rel="noopener noreferrer"
            className="hidden items-center gap-2 rounded-md border border-white/20 px-3 py-2 text-[10px] font-black tracking-widest text-white uppercase transition-all hover:bg-white/10 sm:flex xl:px-4"
            title={`Téléchargez la brochure ${currentSeason}`}
          >
            <Download size={14} />
            <span className="hidden xl:inline">Téléchargez</span>
          </a>
          <Link
            href="/inscription/"
            className="bg-red hover:bg-red-dark hidden rounded-md px-4 py-2 text-[10px] font-black tracking-widest text-white uppercase shadow-lg transition-colors sm:block xl:px-6"
          >
            S&apos;inscrire
          </Link>
          <button
            type="button"
            aria-label={isMobileMenuOpen ? 'Fermer le menu principal' : 'Ouvrir le menu principal'}
            aria-expanded={isMobileMenuOpen}
            aria-controls="mobile-navigation"
            className="text-white lg:hidden"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          >
            {isMobileMenuOpen ? <X size={28} /> : <Menu size={28} />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      <div
        id="mobile-navigation"
        className={cn(
          'bg-navy absolute top-full right-0 left-0 overflow-hidden border-t border-white/10 transition-all duration-300 lg:hidden',
          isMobileMenuOpen ? 'max-h-screen opacity-100' : 'max-h-0 opacity-0 shadow-none',
        )}
      >
        <div className="container mx-auto flex flex-col gap-4 px-4 py-6">
          {navigation.map((item) => (
            <div key={item.label} className="flex flex-col gap-2">
              <Link
                href={item.url}
                className="text-lg font-bold text-white uppercase"
                onClick={(e) => {
                  if (item.url === '#' || item.children.length > 0) {
                    e.preventDefault();
                  } else {
                    setIsMobileMenuOpen(false);
                  }
                }}
              >
                {item.label}
              </Link>
              {item.children.map((child) => (
                <Link
                  key={child.label}
                  href={child.url}
                  className="hover:text-red pl-4 text-sm text-white/70"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  {child.label}
                </Link>
              ))}
            </div>
          ))}
          <Link
            href="/inscription/"
            className="bg-red hover:bg-red-dark mt-4 w-full rounded-md py-4 text-center font-bold text-white uppercase transition-colors"
            onClick={() => setIsMobileMenuOpen(false)}
          >
            S&apos;inscrire
          </Link>
          <a
            href="/images/menu/BrochureDynamicClub26-27.pdf"
            target="_blank"
            rel="noopener noreferrer"
            className="mt-2 flex items-center justify-center gap-2 rounded-md bg-white/5 py-4 font-bold text-white uppercase"
            onClick={() => setIsMobileMenuOpen(false)}
          >
            <Download size={18} /> Téléchargez la brochure
          </a>
        </div>
      </div>
    </header>
  );
}
